import * as React from "react";
import { useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { EditableEffectiveDateRangeAccessRule } from "./models";
import {
  effectiveDateRangeDatesOverlap,
  effectiveDateRangeRuleUniqueKeyWithField,
  validateEffectiveDateRangeRulesForErrors,
  validateEffectiveDateRangeRulesForWarnings,
} from "./module";
import { makeDateFrom } from "../language/time";
import { getResource } from "../language/resources/getResource";

type ValidationMap = Record<string, string[]>;

type ValidationResult = {
  errors: ValidationMap;
  warnings: ValidationMap;
};

const push = (map: ValidationMap, key: string, msg: string) => {
  if (!map[key]) map[key] = [];
  map[key].push(msg);
};

const computeValidation = (
  rules: EditableEffectiveDateRangeAccessRule[],
  resources: Record<string, any>,
  dateFormat: string,
): ValidationResult => {
  const errors: ValidationMap = {};
  const warnings: ValidationMap = {};

  const fields = ["effectiveDate", "expirationDate"] as const;

  // Per-row validation
  rules.forEach((row) => {
    fields.forEach((field) => {
      const key = effectiveDateRangeRuleUniqueKeyWithField(row, field);

      const error = validateEffectiveDateRangeRulesForErrors(
        resources,
        row.season,
        makeDateFrom(row.effectiveDate),
        makeDateFrom(row.expirationDate),
        field,
        dateFormat,
      );

      if (error) {
        push(errors, key, error);
        return;
      }

      const warning = validateEffectiveDateRangeRulesForWarnings(
        resources,
        row.season,
        makeDateFrom(row.effectiveDate),
        makeDateFrom(row.expirationDate),
        field,
        dateFormat,
      );

      if (warning) {
        push(warnings, key, warning);
      }
    });
  });

  // Cross-row validation for overlapping date ranges across fulfillment seasons
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i];
      const b = rules[j];

      // match entitlement type
      if (a.entitlementTypeId !== b.entitlementTypeId) continue;

      fields.forEach((field) => {
        if (effectiveDateRangeDatesOverlap(field, a, b)) {
          const msg = getResource(
            resources,
            "VALIDATION_CONFIG_ERROR_OVERLAPPING_DATES",
          );

          const aEffective = new Date(a.effectiveDate);
          const bEffective = new Date(b.effectiveDate);

          if (aEffective <= bEffective) {
            // a starts first, b starts later
            push(errors, effectiveDateRangeRuleUniqueKeyWithField(a, "expirationDate"), msg,);
            push(errors, effectiveDateRangeRuleUniqueKeyWithField(b, "effectiveDate"), msg,);
          } else {
            // b starts first, a starts later
            push(errors, effectiveDateRangeRuleUniqueKeyWithField(a, "effectiveDate"), msg,);
            push(errors, effectiveDateRangeRuleUniqueKeyWithField(b, "expirationDate"), msg,);
          }
        }
      });
    }
  }

  return { errors, warnings };
};

export const useEffectiveDateRangeValidation = (
  data: EditableEffectiveDateRangeAccessRule[],
) => {
  const resources = useSelector((s: IAppState) => s.locale);
  const dateFormat = useSelector((s: IAppState) => s.defaultDateFormat);

  const result = React.useMemo(
    () => computeValidation(data, resources, dateFormat),
    [data, resources, dateFormat],
  );

  const getKey = (row: EditableEffectiveDateRangeAccessRule, field: string) =>
    effectiveDateRangeRuleUniqueKeyWithField(row, field);

  return {
    getError: (row: EditableEffectiveDateRangeAccessRule, field: string) =>
      result.errors[getKey(row, field)]?.[0],

    getWarning: (row: EditableEffectiveDateRangeAccessRule, field: string) =>
      result.errors[getKey(row, field)]
        ? undefined
        : result.warnings[getKey(row, field)]?.[0],

    hasError: (row: EditableEffectiveDateRangeAccessRule, field: string) =>
      !!result.errors[getKey(row, field)],

    hasWarning: (row: EditableEffectiveDateRangeAccessRule, field: string) =>
      !result.errors[getKey(row, field)] &&
      !!result.warnings[getKey(row, field)],

    raw: result,
  };
};
